/**
 * Data filtering utilities for client and country data
 */

/**
 * Filter country data based on filters
 * @param {Array} data - The country data
 * @param {Object} filters - The filters to apply
 * @returns {Array} The filtered country data
 */
export const filterCountryData = (data, filters) => {
  const { selectedManager, minTotal, minLargest, searchQuery, searchMatchingCountries, selectedYear } = filters;
  
  // Apply year filtering to country data if needed
  let filteredData = data;
  if (selectedYear !== 'All') {
    // Filter out countries that have no clients with bookings in the selected year
    filteredData = data.filter(country => {
      // Check if this country has any clients with bookings in the selected year
      // This is a simplified check - the actual client filtering will handle the detailed logic
      // We just need to exclude countries that would have zero clients after year filtering
      return country.total > 0; // Keep countries that have clients (detailed filtering happens in client data)
    });
  }
  
  // If there's a search query, we need to filter the data more aggressively
  if (searchQuery?.trim()) {
    // Only show countries that have clients matching the search query
    return filteredData.filter(d => {
      // Apply manager, total, and largest filters
      const baseFilters = 
        (selectedManager === 'All' || d.managers.includes(selectedManager)) &&
        d.total >= minTotal &&
        d.largest >= minLargest;
      
      // Only include countries that have clients matching the search query
      return baseFilters && searchMatchingCountries && searchMatchingCountries.has(d.country);
    });
  } else {
    // No search query, just apply the basic filters
    return filteredData.filter(d =>
      (selectedManager === 'All' || d.managers.includes(selectedManager)) &&
      d.total >= minTotal &&
      d.largest >= minLargest
    );
  }
};

/**
 * Filter client data based on search query and year
 * @param {Array} clientData - The client data 
 * @param {string} searchQuery - The search query
 * @param {string} selectedYear - The selected year for History Mode
 * @returns {Array} The filtered client data
 */
export const filterClientData = (clientData, searchQuery, selectedYear = 'All') => {
  let filteredData = clientData;
  
  // Apply year filtering first (History Mode)
  if (selectedYear !== 'All') {
    filteredData = filteredData.filter(client => {
      // Check if client has non-zero values in ALL THREE year-specific columns
      const roomsColumn = `${selectedYear} Rooms`;
      const rnColumn = `${selectedYear} RN`;
      const revenueColumn = `${selectedYear} Revenue`;
      
      const roomsRaw = client[roomsColumn] || 0;
      const rnRaw = client[rnColumn] || 0;
      const revenueRaw = client[revenueColumn] || 0;
      
      // Convert string values to numbers for comparison
      const rooms = typeof roomsRaw === 'string' ? parseFloat(roomsRaw) || 0 : roomsRaw;
      const rn = typeof rnRaw === 'string' ? parseFloat(rnRaw) || 0 : rnRaw;
      // For revenue, remove currency symbols and commas before parsing
      const revenue = typeof revenueRaw === 'string' ? 
        parseFloat(revenueRaw.replace(/[$,]/g, '')) || 0 : revenueRaw;
      
      // Client must have non-zero values in all three columns to be included
      const hasBooking = rooms > 0 && rn > 0 && revenue > 0;
      return hasBooking;
    });
  }
  
  // Apply search query filtering
  if (!searchQuery?.trim()) return filteredData;
  
  const query = searchQuery.toLowerCase().trim();
  
  // Check if the search query is a tier-based search
  const tierPatterns = [
    { pattern: /^tier\s*1$/i, value: '1' },
    { pattern: /^tier\s*2$/i, value: '2' },
    { pattern: /^tier\s*3$/i, value: '3' },
    { pattern: /^tier\s*vip$/i, value: 'VIP' }
  ];
  
  // Check if query matches any tier pattern
  for (const tierPattern of tierPatterns) {
    if (tierPattern.pattern.test(query)) {
      return filteredData.filter(client => 
        client.Tier === tierPattern.value || client['Tier'] === tierPattern.value
      );
    }
  }
  
  // If not a tier search, filter by client name as before
  return filteredData.filter(client => 
    client.client_name && client.client_name.toLowerCase().includes(query)
  );
};
